/* eslint-disable react/no-unescaped-entities */
/**
 * PILIER 2 SEO — Pages Problématiques (entreprises + particuliers).
 *
 * 18 landings haute intention pour des recherches type :
 *   « contrôle URSSAF », « facture impayée », « divorce », « succession »…
 *
 * Chaque page = positionnement institutionnel CONFILEX (structuration documentaire)
 * + orientation vers la profession adaptée + Vérificateur + FAQ.
 */
import { Link } from "react-router-dom";
import {
  AlertTriangle, Compass, ScanLine, GitMerge, ShieldCheck, ArrowRight,
  Receipt, Scale, Briefcase, FileSpreadsheet, Building2, Users, Home as HomeIcon,
  Coins, Heart, Landmark, FileText, BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Seo, { ldBreadcrumb, ldFAQ } from "@/components/Seo";


export const PROBLEMATIQUE_PAGES = {
  // ===== ENTREPRISES =====
  "controle-urssaf": {
    audience: "entreprise",
    icon: AlertTriangle,
    label: "Contrôle URSSAF",
    h1: "Faire face à un contrôle URSSAF",
    seo_title: "Contrôle URSSAF — Préparer son dossier · CONFILEX",
    seo_description: "Avis de passage URSSAF, demande de pièces, proposition de redressement : structurez votre dossier et trouvez l'avocat ou expert-comptable adapté.",
    intent: ["contrôle URSSAF", "redressement URSSAF", "URSSAF mise en demeure"],
    pieces: ["Avis de passage URSSAF", "Demande de pièces", "Bulletins de paie sur la période", "DSN et déclarations sociales", "Contrats de travail", "Conventions de prestations / sous-traitance"],
    professionals: ["avocat-fiscal", "avocat-societes", "expert-comptable-pme"],
  },
  "controle-fiscal": {
    audience: "entreprise",
    icon: AlertTriangle,
    label: "Contrôle fiscal",
    h1: "Faire face à un contrôle fiscal",
    seo_title: "Contrôle fiscal entreprise — Préparer son dossier · CONFILEX",
    seo_description: "Proposition de rectification, vérification de comptabilité, examen contradictoire : structurez votre dossier et trouvez le professionnel adapté.",
    intent: ["contrôle fiscal", "vérification de comptabilité", "proposition de rectification"],
    pieces: ["Avis de vérification", "Proposition de rectification (2120-SD)", "Comptes annuels et FEC", "Déclarations fiscales (IS, TVA, CFE, CVAE)", "Correspondance avec l'administration"],
    professionals: ["avocat-fiscal", "expert-comptable-pme"],
  },
  "contentieux-commercial": {
    audience: "entreprise",
    icon: Scale,
    label: "Contentieux commercial",
    h1: "Préparer un contentieux commercial",
    seo_title: "Contentieux commercial — Préparation dossier · CONFILEX",
    seo_description: "Différend commercial avec un client, fournisseur ou partenaire : structurez votre dossier (contrats, factures, courriers) et orientez-vous.",
    intent: ["contentieux commercial", "litige commercial", "tribunal de commerce"],
    pieces: ["Contrat / commande signée", "Conditions générales", "Factures et bons de livraison", "Mises en demeure échangées", "Courriels et correspondance"],
    professionals: ["avocat-commercial", "avocat-societes"],
  },
  "facture-impayee": {
    audience: "entreprise",
    icon: Receipt,
    label: "Facture impayée",
    h1: "Gérer une facture impayée",
    seo_title: "Facture impayée — Recouvrement et négociation · CONFILEX",
    seo_description: "Facture impayée : structurez votre dossier (commande, livraison, mise en demeure) et orientez-vous vers l'avocat ou commissaire de justice adapté.",
    intent: ["facture impayée", "client qui ne paie pas", "recouvrement créance"],
    pieces: ["Devis ou bon de commande accepté", "Facture impayée", "Bon de livraison signé", "Relances envoyées", "Mise en demeure"],
    professionals: ["avocat-commercial", "avocat-recouvrement", "negociation-creances"],
  },
  "recouvrement": {
    audience: "entreprise",
    icon: Coins,
    label: "Recouvrement de créances",
    h1: "Préparer un recouvrement de créances",
    seo_title: "Recouvrement de créances — Structuration dossier · CONFILEX",
    seo_description: "Dossier de recouvrement : créance, mises en demeure, factures, échanges. CONFILEX vous oriente vers l'avocat ou commissaire de justice adapté.",
    intent: ["recouvrement créances", "recouvrement amiable", "huissier recouvrement"],
    pieces: ["Contrat ou commande", "Facture impayée", "Mises en demeure", "Relances", "Justificatifs de livraison"],
    professionals: ["avocat-commercial", "negociation-creances"],
  },
  "pacte-associes": {
    audience: "entreprise",
    icon: Users,
    label: "Pacte d'associés",
    h1: "Préparer ou réviser un pacte d'associés",
    seo_title: "Pacte d'associés — Préparer le dossier · CONFILEX",
    seo_description: "Pacte d'associés à rédiger ou à réviser (préemption, agrément, sortie, drag/tag, leaver). Préparez votre dossier et identifiez l'avocat adapté.",
    intent: ["pacte d'associés", "rédiger pacte associés", "conflit associés"],
    pieces: ["Statuts à jour", "Précédent pacte (s'il existe)", "K-bis et capital", "Comptes annuels", "Conventions intra-groupe"],
    professionals: ["avocat-societes", "avocat-affaires"],
  },
  "creation-sas": {
    audience: "entreprise",
    icon: Briefcase,
    label: "Création SAS",
    h1: "Préparer la création d'une SAS",
    seo_title: "Création de SAS — Préparer le dossier · CONFILEX",
    seo_description: "Création SAS / SASU : statuts, capital, dirigeant, BSPCE, pacte d'associés. CONFILEX vous oriente vers l'avocat ou expert-comptable adapté.",
    intent: ["création SAS", "créer SAS", "statuts SAS"],
    pieces: ["Projet de statuts", "Justificatif de domiciliation", "Justificatif d'apport en capital", "Pièces d'identité des associés", "Projet de pacte (le cas échéant)"],
    professionals: ["expert-comptable-sas", "avocat-societes"],
  },
  "creation-holding": {
    audience: "entreprise",
    icon: Building2,
    label: "Création holding",
    h1: "Préparer la création d'une holding",
    seo_title: "Création de holding — Préparer le dossier · CONFILEX",
    seo_description: "Création d'une holding patrimoniale, animatrice ou financière : structurez votre dossier et trouvez l'avocat ou expert-comptable adapté.",
    intent: ["création holding", "holding patrimoniale", "régime mère-fille"],
    pieces: ["Projet de structuration (organigramme)", "Statuts envisagés", "Comptes annuels des entités existantes", "Projet de pacte d'associés", "Étude patrimoniale"],
    professionals: ["expert-comptable-sas", "avocat-fiscal", "avocat-societes"],
  },
  "transmission-entreprise": {
    audience: "entreprise",
    icon: ArrowRight,
    label: "Transmission d'entreprise",
    h1: "Préparer la transmission de votre entreprise",
    seo_title: "Transmission d'entreprise — Préparer le dossier · CONFILEX",
    seo_description: "Cession, donation, LBO familial, pacte Dutreil : structurez votre dossier de transmission et trouvez l'avocat, expert-comptable et notaire adaptés.",
    intent: ["transmission entreprise", "cession entreprise", "pacte Dutreil"],
    pieces: ["Statuts et K-bis", "Comptes annuels des 3 derniers exercices", "Précédent pacte d'associés", "Évaluation de l'entreprise", "Étude patrimoniale familiale"],
    professionals: ["avocat-fiscal", "avocat-societes", "notaire-investissement", "expert-comptable-pme"],
  },
  "audit-juridique": {
    audience: "entreprise",
    icon: BookOpen,
    label: "Audit juridique",
    h1: "Préparer un audit juridique d'entreprise",
    seo_title: "Audit juridique — Préparer le dossier · CONFILEX",
    seo_description: "Audit juridique de votre société (statuts, contrats, conformité). CONFILEX structure vos pièces avant transmission à votre avocat.",
    intent: ["audit juridique", "due diligence juridique", "vendor due diligence"],
    pieces: ["Statuts et K-bis", "Pacte d'associés", "Contrats majeurs (clients, fournisseurs, salariés)", "Procédures contentieuses en cours", "Conformité RGPD"],
    professionals: ["avocat-affaires", "avocat-societes"],
  },
  "audit-comptable": {
    audience: "entreprise",
    icon: FileSpreadsheet,
    label: "Audit comptable",
    h1: "Préparer un audit comptable",
    seo_title: "Audit comptable — Préparer le dossier · CONFILEX",
    seo_description: "Audit comptable légal ou contractuel : structurez vos comptes, balance, FEC et annexes. CONFILEX vous oriente vers l'expert-comptable adapté.",
    intent: ["audit comptable", "commissaire aux comptes", "due diligence financière"],
    pieces: ["Comptes annuels et liasses fiscales", "Balance générale", "FEC", "Conventions réglementées", "Rapport de gestion"],
    professionals: ["expert-comptable-pme", "expert-comptable-immobilier"],
  },

  // ===== PARTICULIERS =====
  "succession": {
    audience: "particulier",
    icon: Heart,
    label: "Succession",
    h1: "Préparer une succession",
    seo_title: "Succession — Préparer le dossier · CONFILEX",
    seo_description: "Décès d'un proche, succession à régler : structurez votre dossier (testament, comptes, biens, dettes) et trouvez le notaire et l'avocat adaptés.",
    intent: ["succession", "règlement succession", "héritage"],
    pieces: ["Acte de décès", "Testament éventuel", "Livret de famille", "Inventaire patrimonial (biens, comptes, dettes)", "Précédents actes notariés"],
    professionals: ["notaire-succession", "avocat-famille"],
  },
  "divorce": {
    audience: "particulier",
    icon: Users,
    label: "Divorce",
    h1: "Préparer un dossier de divorce",
    seo_title: "Divorce — Préparer le dossier · CONFILEX",
    seo_description: "Divorce amiable ou contentieux : structurez votre dossier (contrat de mariage, revenus, patrimoine, enfants) et trouvez l'avocat adapté.",
    intent: ["divorce", "avocat divorce", "divorce amiable"],
    pieces: ["Contrat de mariage / PACS", "Justificatifs de revenus", "Inventaire patrimonial", "Convention parentale envisagée", "Documents enfants"],
    professionals: ["avocat-famille", "notaire-donation"],
  },
  "licenciement": {
    audience: "particulier",
    icon: Briefcase,
    label: "Licenciement",
    h1: "Faire face à un licenciement",
    seo_title: "Licenciement — Préparer le dossier · CONFILEX",
    seo_description: "Licenciement reçu ou envisagé : structurez votre dossier (contrat, notifications, bulletins de paie) et trouvez l'avocat en droit du travail adapté.",
    intent: ["licenciement", "avocat licenciement", "prud'hommes licenciement"],
    pieces: ["Contrat de travail et avenants", "Lettre de licenciement / convocation", "Bulletins de paie", "Convention collective", "Courriers et notifications RH"],
    professionals: ["avocat-droit-du-travail"],
  },
  "bail-locatif": {
    audience: "particulier",
    icon: HomeIcon,
    label: "Bail locatif",
    h1: "Litige ou question sur un bail locatif",
    seo_title: "Bail locatif — Préparer le dossier · CONFILEX",
    seo_description: "Litige locatif (loyer, charges, dépôt, troubles), résiliation, congé : structurez votre dossier et trouvez l'avocat adapté.",
    intent: ["bail locatif", "litige bail", "avocat bail"],
    pieces: ["Bail signé", "État des lieux", "Quittances de loyer", "Diagnostics", "Courriers échangés"],
    professionals: ["avocat-immobilier"],
  },
  "copropriete": {
    audience: "particulier",
    icon: Building2,
    label: "Copropriété",
    h1: "Litige ou question de copropriété",
    seo_title: "Copropriété — Préparer le dossier · CONFILEX",
    seo_description: "Litige de copropriété (charges, AG, travaux, syndic) : structurez vos PV d'AG, appels de charges et règlement et trouvez l'avocat adapté.",
    intent: ["copropriété", "litige copropriété", "syndic copropriété"],
    pieces: ["PV d'assemblées générales", "Règlement de copropriété", "Appels de charges", "Contrat de syndic", "Devis et factures travaux"],
    professionals: ["avocat-immobilier"],
  },
  "fiscalite-personnelle": {
    audience: "particulier",
    icon: Landmark,
    label: "Fiscalité personnelle",
    h1: "Préparer un dossier de fiscalité personnelle",
    seo_title: "Fiscalité personnelle — Préparer le dossier · CONFILEX",
    seo_description: "IR, IFI, plus-value, contrôle fiscal personnel : structurez votre dossier et trouvez l'avocat fiscaliste ou expert-comptable adapté.",
    intent: ["fiscalité personnelle", "contrôle fiscal particulier", "IFI plus-value"],
    pieces: ["Avis d'imposition", "Déclarations 2042 et annexes", "Proposition de rectification éventuelle", "Justificatifs patrimoniaux"],
    professionals: ["avocat-fiscal", "expert-comptable-immobilier"],
  },
  "donation": {
    audience: "particulier",
    icon: FileText,
    label: "Donation",
    h1: "Préparer une donation",
    seo_title: "Donation — Préparer le dossier · CONFILEX",
    seo_description: "Donation entre époux, aux enfants, donation-partage, démembrement : structurez votre dossier et trouvez le notaire adapté.",
    intent: ["donation", "donation enfants", "donation-partage"],
    pieces: ["Pièces d'identité du donateur et des bénéficiaires", "Évaluation des biens à donner", "Précédentes donations éventuelles", "Étude patrimoniale familiale"],
    professionals: ["notaire-donation"],
  },
};


export default function ProblematiquePage({ slug }) {
  const cfg = PROBLEMATIQUE_PAGES[slug];
  if (!cfg) return <div className="p-20 text-center">Page non trouvée.</div>;
  const Icon = cfg.icon || AlertTriangle;

  const faq = [
    {
      q: `CONFILEX peut-il m'aider sur ${cfg.label.toLowerCase()} ?`,
      a: `CONFILEX vous aide à structurer votre dossier : inventaire des pièces utiles, lecture documentaire factuelle, chronologie des événements. La consultation et l'analyse juridique restent du ressort exclusif d'un professionnel qualifié.`,
    },
    {
      q: `Quels documents préparer en priorité ?`,
      a: `Les pièces les plus utiles sont listées plus haut dans cette page. Le Vérificateur CONFILEX produit ensuite une lecture documentaire factuelle pour vous aider à transmettre un dossier déjà structuré au professionnel.`,
    },
    {
      q: `Comment CONFILEX m'oriente-t-il vers le bon professionnel ?`,
      a: `Après qualification documentaire de votre dossier, CONFILEX vous propose des profils dont la spécialisation correspond. Vous restez libre de choisir et de négocier les honoraires directement avec le professionnel.`,
    },
    {
      q: `Quel est le coût ?`,
      a: `L'orientation initiale est gratuite. Le Vérificateur (lecture factuelle d'un document) est facturé 29 à 49 €. La Mission de Recherche Premium est facturée 89 € TTC. CONFILEX ne perçoit aucune commission sur les honoraires des professionnels.`,
    },
    {
      q: `Mon dossier est-il confidentiel ?`,
      a: `Oui — architecture Zero Retention, chiffrement en transit (TLS), absence de réutilisation de vos pièces par l'IA, purge automatique des sessions Vérificateur après 90 jours.`,
    },
  ];

  return (
    <>
      <Seo
        title={cfg.seo_title}
        description={cfg.seo_description}
        canonical={`/${slug}`}
        keywords={cfg.intent}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": cfg.h1,
            "description": cfg.seo_description,
            "provider": { "@type": "Organization", "name": "CONFILEX", "url": "https://confilex.fr" },
            "areaServed": { "@type": "Country", "name": "France" },
            "serviceType": cfg.label,
          },
          ldBreadcrumb([
            { name: "Accueil", url: "/" },
            { name: cfg.audience === "entreprise" ? "Entreprises" : "Particuliers", url: "/" },
            { name: cfg.h1, url: `/${slug}` },
          ]),
          ldFAQ(faq),
        ]}
      />

      <main className="bg-paper min-h-screen" data-testid={`problematique-${slug}`}>
        <section className="bg-night text-paper">
          <div className="max-w-5xl mx-auto px-6 py-16">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold inline-flex items-center gap-2">
              <Icon className="w-3.5 h-3.5" />
              CONFILEX · {cfg.audience === "entreprise" ? "ENTREPRISES" : "PARTICULIERS"}
            </p>
            <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl leading-tight mb-5" data-testid="page-h1">
              {cfg.h1}
            </h1>
            <p className="text-paper/75 text-base max-w-3xl leading-relaxed mb-6">
              CONFILEX vous aide à structurer votre dossier avant toute consultation et
              à transmettre un dossier exploitable au professionnel qualifié. Vous gagnez
              en clarté, le professionnel gagne du temps.
            </p>
            <div className="flex gap-3 flex-wrap">
              <Link to="/orientation-intelligente">
                <Button className="bg-copper text-paper hover:bg-copper/90 rounded-sm" data-testid="cta-orientation">
                  <Compass className="w-4 h-4 mr-2" /> Trouver le bon professionnel
                </Button>
              </Link>
              <Link to="/verifier">
                <Button variant="outline" className="border-paper/40 text-paper hover:bg-paper/10 rounded-sm" data-testid="cta-verifier">
                  <ScanLine className="w-4 h-4 mr-2" /> Préparer mon dossier
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-6 py-12">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">PIÈCES UTILES</p>
          <h2 className="font-serif italic text-3xl text-night mb-6">Quelles pièces préparer ?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {cfg.pieces.map((p, i) => (
              <Card key={i} className="p-4 border-sage hover:border-copper transition-smooth" data-testid={`piece-${i}`}>
                <FileText className="w-4 h-4 text-copper mb-2" />
                <p className="text-sm text-night leading-snug">{p}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="bg-ivory border-t border-sage">
          <div className="max-w-5xl mx-auto px-6 py-12">
            <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">PROFESSIONNELS ADAPTÉS</p>
            <h2 className="font-serif italic text-3xl text-night mb-6">
              Quel professionnel consulter ?
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cfg.professionals.map((p) => (
                <Link key={p} to={`/${p}`} className="block p-4 border border-sage rounded-sm hover:border-copper bg-paper transition-smooth" data-testid={`pro-${p}`}>
                  <Compass className="w-4 h-4 text-copper mb-2" />
                  <p className="text-sm font-medium text-night">{p.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</p>
                  <p className="text-[11px] text-night/60 mt-1 inline-flex items-center">
                    Trouver ce professionnel <ArrowRight className="w-3 h-3 ml-1" />
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 py-12" data-testid="page-faq">
          <p className="text-[11px] tracking-[0.28em] uppercase text-copper mb-3 font-semibold">QUESTIONS FRÉQUENTES</p>
          <h2 className="font-serif italic text-3xl text-night mb-6">À savoir</h2>
          <div className="space-y-4">
            {faq.map((item, i) => (
              <Card key={i} className="p-5 border-sage">
                <h3 className="font-semibold text-night mb-2">{item.q}</h3>
                <p className="text-sm text-night/75 leading-relaxed">{item.a}</p>
              </Card>
            ))}
          </div>
        </section>

        <section className="border-t border-sage bg-paper">
          <div className="max-w-5xl mx-auto px-6 py-8 flex items-start gap-3 text-sm text-night/75">
            <ShieldCheck className="w-5 h-5 text-copper flex-shrink-0 mt-0.5" />
            <p>
              CONFILEX est une plateforme de structuration documentaire et d'orientation.
              CONFILEX ne fournit aucune consultation juridique, fiscale, comptable, notariale
              ou financière. Les professionnels référencés exercent en toute indépendance.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}
